import React, { useMemo, useState } from "react";

import { Button } from "../../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import {
  buildPlacementEmbedSnippet,
  formatEmbedModeLabel,
  formatPurposeLabel,
} from "./publicFormsConfig";

export default function PublicFormEmbeddingPanel({
  baseUrl,
  form,
  placements,
  widgetEmbedEnabled,
  onCopy,
}) {
  const [selectedPlacementId, setSelectedPlacementId] = useState("");

  const currentPlacement = useMemo(() => {
    if (!placements.length) {
      return null;
    }
    return placements.find((placement) => placement.id === selectedPlacementId) || placements[0];
  }, [placements, selectedPlacementId]);

  const standaloneUrl = form?.form_key ? `${baseUrl}/demo/${form.form_key}` : "";
  const snippet = currentPlacement
    ? buildPlacementEmbedSnippet(baseUrl, currentPlacement.placement_key)
    : "";
  const hasExternalAllowedOrigins = Boolean(currentPlacement?.allowed_origins?.length);

  return (
    <Card className="border-border/70 bg-card/80 shadow-sm">
      <CardHeader>
        <CardTitle>Einbettung</CardTitle>
        <CardDescription>
          Verwalten Sie Standalone-Link, Website-Snippet und technische Details getrennt von der Einsatzorte-Liste.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-xl border border-border/70 bg-card/60 p-4 space-y-3">
          <div className="text-sm font-medium">Standalone-Link</div>
          <div className="text-sm text-muted-foreground">
            Nutzen Sie den Standalone-Link für eine direkte Formular-Seite ohne Website-Einbettung.
          </div>
          <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 font-mono text-xs break-all">
            {standaloneUrl || "Kein Standalone-Link verfügbar"}
          </div>
          {standaloneUrl ? (
            <Button type="button" variant="outline" onClick={() => onCopy(standaloneUrl, "Standalone-Link")}>
              Link kopieren
            </Button>
          ) : null}
        </div>

        <div className="rounded-xl border border-border/70 bg-card/60 p-4 space-y-3">
          <div className="text-sm font-medium">Website-Einbettung</div>
          <div className="text-sm text-muted-foreground">
            Bitte nur auf Websites verwenden, deren Datenschutz- und Impressumsangaben korrekt hinterlegt sind.
          </div>

          {placements.length ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="forms-embedding-placement">
                  Einsatzort
                </label>
                <select
                  id="forms-embedding-placement"
                  data-testid="forms-embedding-placement-select"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={currentPlacement?.id || ""}
                  onChange={(event) => setSelectedPlacementId(event.target.value)}
                >
                  {placements.map((placement) => (
                    <option key={placement.id} value={placement.id}>
                      {placement.name || "Unbenannter Einsatzort"}
                    </option>
                  ))}
                </select>
              </div>

              {currentPlacement ? (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-3">
                    <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-3 space-y-2">
                      <div className="text-xs text-muted-foreground">Zweck</div>
                      <div>{formatPurposeLabel(currentPlacement.purpose_key)}</div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-3 space-y-2">
                      <div className="text-xs text-muted-foreground">Quelle</div>
                      <div>{currentPlacement.source_label || "Nicht gesetzt"}</div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-3 space-y-2">
                      <div className="text-xs text-muted-foreground">Einbettungsart</div>
                      <div>{formatEmbedModeLabel(currentPlacement.embed_mode)}</div>
                    </div>
                    {currentPlacement.submit_test_mode ? (
                      <div className="rounded-lg border border-amber-500/40 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                        Testmodus aktiv – Mails werden unterdrückt
                      </div>
                    ) : null}

                    {widgetEmbedEnabled ? (
                      <>
                        <div className="text-sm text-muted-foreground">
                          Website-Einbettung ist für diesen Einsatzort verfügbar.
                        </div>
                        {!hasExternalAllowedOrigins ? (
                          <div className="rounded-lg border border-dashed border-border/80 p-3 text-sm text-muted-foreground">
                            <div>Für externe Websites ist noch keine Origin freigegeben.</div>
                            <div>
                              Cross-Origin-Einbettungen funktionieren erst nach Freigabe einer
                              Website-Origin.
                            </div>
                          </div>
                        ) : null}
                        <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border/60 bg-background px-3 py-3 font-mono text-xs text-muted-foreground">
                          {snippet}
                        </pre>
                        <Button type="button" variant="outline" onClick={() => onCopy(snippet, "Einbettungs-Snippet")}>
                          Snippet kopieren
                        </Button>
                      </>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border/80 p-3 text-sm text-muted-foreground">
                        Website-Einbettung ist in diesem Tarif nicht aktiv.
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-3">
                      <div className="mb-1 text-xs font-medium text-foreground/80">Technischer Schlüssel</div>
                      <div className="break-all font-mono text-xs text-muted-foreground">
                        {currentPlacement.placement_key}
                      </div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-3">
                      <div className="mb-1 text-xs font-medium text-foreground/80">Asset-Schlüssel</div>
                      <div className="break-all text-xs text-muted-foreground">
                        {currentPlacement.asset_key || "Nicht gesetzt"}
                      </div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-3">
                      <div className="mb-1 text-xs font-medium text-foreground/80">Kampagnen-Schlüssel</div>
                      <div className="break-all text-xs text-muted-foreground">
                        {currentPlacement.campaign_key || "Nicht gesetzt"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border/80 p-3 text-sm text-muted-foreground">
              Es sind noch keine Einsatzorte für dieses Formular vorhanden.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
